import { DataAccess } from "../../../dataaccess/data.access";
import { S25Array } from "../../../util/core/s25.array";
import { S25Date } from "../../../util/core/s25.date";
import { S25ObjectUtil } from "../../../util/core/s25.object.util";
import { S25Xml } from "../../../util/core/s25.xml";
import { S25UUID } from "../../../util/core/s25.uuid";
import { Proto } from "../../../pojo/Proto";
import { Event } from "../../../pojo/Event";
import { PaginateParams } from "../../../pojo/S25WsNode";
import { Category } from "../../../pojo/Category";
import { Organization } from "../../../pojo/Organization";
import { OrganizationService } from "../../../services/organization.service";
import { SearchCriteria } from "../../../pojo/SearchCriteria";
import { attendanceState, AttendanceState } from "../attendance/attendance.service";
import { isDefined } from "../../../util/core";

/**
 * Document object for Student Engagement organization search results.
 * Contains core organization fields plus SE-specific custom attributes.
 */
export type SEOrganizationDocument = {
    itemId: number;
    itemName: string;
    itemTypeId: number;
    txt: string;
    title?: string;
    logo?: string; //holds the URL from the server
    logoUrl?: Proto.URLString; //property name used everywhere else //TODO separate search results type from server format
    mission?: string;
    // Admin-specific fields (from server)
    type?: string;
    members?: {
        contactId: number;
        contactName: string;
        email?: string;
        role?: { roleId: number; roleName: string };
    }[];
    primaryContact?: {
        contactId: number;
        contactName: string;
        email?: string;
    };
    rating?: { ratingId: number; ratingName: string; organizationStanding?: "allow" | "deny" | "warn" };
    categories?: { categoryId: number; categoryName: string }[];
    eventCount?: number;
    lastModified?: string;
    userRoles?: { roleId: number; roleName: string }[];
    favorite?: boolean; // null when signed out
};

export type SESearchResponse<T extends SEOrganizationDocument | SEEventDocument> = Proto.Paginate & {
    items: T[];
};

export type SEOrganizationSearchResponse = Proto.Paginate & {
    organizations: SEOrganizationDocument[];
};

export type SEEventSearchResponse = Proto.Paginate & {
    events: SEEventDocument[];
};

/**
 * Document object for Student Engagement event search results.
 * Contains core event fields plus SE-specific custom attributes, reservation data, and host organization.
 * Matches EventTileDocument from backend.
 */
export interface SEEventDocument {
    itemId: number;
    itemName: string;
    eventLocator?: string; // Event locator for external references
    itemTypeId: number;
    txt: string;
    title?: string;
    imageUrl?: string;
    hostOrgLogoUrl?: string;
    startDate: Proto.ISODateString;
    endDate?: Proto.ISODateString;
    spaces?: Space[]; // Array of space objects with ID and name
    description?: string; // Event description text
    date?: string; // Formatted date string for display
    imageAttributeId?: number; // Custom attribute ID for image
    hostOrgId?: number; // Primary organization ID
    hostOrgName?: string; // Primary organization name
    additionalOrganizations?: Space[];
    reservationId?: number;
    occurrences?: EventOccurrence[]; // Reservation occurrences
    lastModified?: string;
}

/**
 * Represents a space/location with ID and name.
 * Follows S25ItemI pattern. Can be extended in the future with lat/long coordinates.
 */
export interface Space {
    itemId?: number;
    itemName: string;
}

/**
 * Represents an event occurrence (reservation) with start/end dates and spaces.
 */
export interface EventOccurrence {
    startDate?: string; // ISO date-time string
    endDate?: string; // ISO date-time string
    spaces: Space[]; // Space objects for this occurrence
    reservationId: number;
    attendanceState: AttendanceState;
}

/** Tile grid page size; multiple of 3 so common 3-column layouts fill complete rows. */
export const DEFAULT_PAGE_SIZE = 12;
/** ACCL reuses a cached org result for later pages only within this window; 30 is its maximum. */
const ORG_TILE_CACHE_REUSE_SECONDS = 30;

/**
 * Service for Student Engagement search functionality.
 * Handles searching for SE organizations and events with appropriate filters.
 */
export class SeSearchService {
    /**
     * Search for Student Engagement organizations with pagination.
     * Uses the dedicated SE organizations endpoint that returns enriched data
     * including logo, title, and mission.
     *
     * @param searchTerm - Search term for organization name (optional)
     * @param page - Page number, 1-based (optional, default 1)
     * @param pageSize - Number of results per page (optional, default 20)
     * @param paginateKey - Pagination key for fetching subsequent pages (optional, default "T")
     * @returns Promise resolving to search response with organizations and pagination metadata
     */
    public static async searchOrganizations(
        searchTerm: string,
        page: number = 1,
        pageSize: number = 20,
        paginateKey: string = "T",
    ): Promise<SESearchResponse<SEOrganizationDocument>> {
        try {
            const queryParams: Organization.WsQueryParams = {
                name: searchTerm.trim(),
            };

            return SeSearchService.getOrganizations(queryParams, {
                page: page,
                page_size: pageSize,
                paginate: paginateKey,
            });
        } catch (error) {
            console.error("Error searching SE organizations:", error);
            throw error;
        }
    }

    public static async getOrganizations(
        queryParams: Organization.WsQueryParams,
        paginateParams: PaginateParams = { page: 1, page_size: 20, paginate: "T" },
    ): Promise<SESearchResponse<SEOrganizationDocument>> {
        try {
            queryParams.sort ??= "name";
            queryParams.order ??= "asc";
            const { paginate: paginateKey, ...pageParams } = paginateParams;
            const { name, ...remainingParams } = queryParams;
            const queryString = SeSearchService.appendKeywordParam(
                DataAccess.params({
                    ...remainingParams,
                    ...pageParams,
                    obj_cache_accl: paginateKey === "T" ? 0 : ORG_TILE_CACHE_REUSE_SECONDS,
                }),
                "name",
                name,
            );
            const response = await DataAccess.get<SEOrganizationSearchResponse>(
                DataAccess.injectCaller(`/tile/organizations.json?${queryString}`, "SeSearchService.getOrganizations"),
            );

            const organizations = await Promise.all(
                S25Array.forceArray(response.organizations)?.map(async (org) => {
                    org.logoUrl = org.logo;
                    if (org.rating) {
                        org.rating.organizationStanding = await OrganizationService.getRatingStanding({
                            itemId: org.itemId,
                            itemName: org.itemName,
                            ratingId: org.rating.ratingId,
                        });
                    }
                    return org;
                }) ?? [],
            );

            const { organizations: _, ...restResponse } = response;

            return {
                ...restResponse,
                items: organizations,
            };
        } catch (error) {
            console.error("Error fetching SE organizations:", error);
            throw error;
        }
    }

    /**
     * Search for Student Engagement events with pagination.
     * Uses the dedicated SE events endpoint that returns enriched data
     * including image, title, dates, and locations.
     *
     * @param queryParams - Query parameters for the event search
     * @param paginateParams - Pagination parameters for the event search
     * @returns Promise resolving to search response with events and pagination metadata
     */
    /**
     * Occurrence tile search for Connect25 event lists. Connect25 Private visibility is enforced
     * server-side ({@code private_requires_org_role=T}). Anonymous viewers also get {@code public_events=T}
     * on {@code /tile/reservations.json} and {@code /tile/events.json}, and {@code public_orgs=T} on
     * {@code /tile/organizations.json} (ANG-7744). {@code category_id} lists stay plus-separated (ANY).
     */
    public static async searchEvents(
        queryParams: Event.WsQueryParams,
        paginateParams: PaginateParams = { page: 1, page_size: 20, paginate: "T" }, //Default to pagination
    ): Promise<SESearchResponse<SEEventDocument>> {
        try {
            // Default to future occurrences if no other date is specified ({@code p_reservations_get} uses start_dt/end_dt).
            if (!SeSearchService.hasDateFilter(queryParams)) {
                queryParams.start_dt = S25Date.toS25ISODateStrStartOfDay(new Date());
            }

            const { organization_id, state, event_name, ...remainingParams } = queryParams;
            let queryString = SeSearchService.appendKeywordParam(
                DataAccess.params({ ...remainingParams, ...paginateParams }),
                "event_name",
                event_name,
            );
            // ACCL OR filter expects plus-separated values (not URL-encoded from DataAccess.params).
            if (organization_id?.length) {
                const organizationIdList = S25Array.forceArray(organization_id);
                queryString += `${queryString ? "&" : ""}organization_id=${organizationIdList.join("+")}`;
            }
            if (state?.length) {
                const stateList = S25Array.forceArray(state);
                queryString += `${queryString ? "&" : ""}state=${stateList.join(",")}`;
            }

            const response = await DataAccess.get<SEEventSearchResponse>(
                DataAccess.injectCaller(`/tile/reservations.json?${queryString}`, "SeSearchService.searchEvents"),
            );

            //normalize response so it is easy to find the events everywhere
            const { events, ...restResponse } = response;

            return {
                ...restResponse,
                items: events && events.length > 0 ? events : [],
            };
        } catch (error) {
            console.error("Error searching SE events:", error);
            throw error;
        }
    }

    /**
     * DataAccess.params encodes spaces as "+", which ACCL keeps as a literal "+" and so cannot split into
     * separate keywords. encodeURIComponent sends "%20", which reaches ACCL as a real space.
     */
    private static appendKeywordParam(queryString: string, key: string, keyword: string | undefined): string {
        if (!isDefined(keyword)) return queryString;
        return `${queryString}${queryString ? "&" : ""}${key}=${encodeURIComponent(keyword)}`;
    }

    public static hasDateFilter(queryParams: Event.WsQueryParams): boolean {
        return !!queryParams.start_dt || !!queryParams.end_dt;
    }

    /**
     * ANG-7744: Whether this event appears in Connect25 portal tile search for the current viewer.
     * Uses {@code /tile/events.json} (not reservations — occurrence date filters can false-negative).
     */
    public static async isConnect25EventVisibleInPortalTile(eventId: number): Promise<boolean> {
        const queryString = DataAccess.params({
            event_id: eventId,
            page: 1,
            page_size: 1,
            paginate: "F",
        });
        const response = await DataAccess.get<SEEventSearchResponse>(
            DataAccess.injectCaller(
                `/tile/events.json?${queryString}`,
                "SeSearchService.isConnect25EventVisibleInPortalTile",
            ),
        );
        return response.events?.some((event) => event.itemId === eventId) === true;
    }

    /**
     * ANG-7744: Whether this organization appears in Connect25 portal tile search for the current viewer.
     */
    public static async isConnect25OrganizationVisibleInPortalTile(orgId: number): Promise<boolean> {
        const queryString = DataAccess.params({
            organization_id: orgId,
            page: 1,
            page_size: 1,
            paginate: "F",
        });
        const response = await DataAccess.get<SEOrganizationSearchResponse>(
            DataAccess.injectCaller(
                `/tile/organizations.json?${queryString}`,
                "SeSearchService.isConnect25OrganizationVisibleInPortalTile",
            ),
        );
        return response.organizations?.some((organization) => organization.itemId === orgId) === true;
    }

    /**
     * Search for Student Engagement events with pagination.
     * Uses the dedicated SE events endpoint that returns enriched data
     * including image, title, dates, and locations.
     *
     * @param searchTerm - Search term for event name (optional)
     * @param page - Page number, 1-based (optional, default 1)
     * @param pageSize - Number of results per page (optional, default 20)
     * @returns Promise resolving to search response with events and pagination metadata
     */
    public static async searchEventsByName(
        searchTerm: string,
        page: number = 1,
        pageSize: number = 20,
        paginateKey: string = "T",
    ): Promise<SESearchResponse<SEEventDocument>> {
        try {
            const SixMonthsAgo = S25Date.addMonths(new Date(), -6);
            // Build query parameters
            const queryParams: Event.WsQueryParams = {
                category_id: [Category.SystemDefined.StudentEngagement],
                start_dt: S25Date.toS25ISODateStrStartOfDay(SixMonthsAgo),
                order: "asc",
                sort: "start_date",
                state: [Event.State.Ids.Confirmed, Event.State.Ids.Tentative, Event.State.Ids.Draft],
            };

            if (searchTerm && searchTerm.trim()) {
                queryParams.event_name = searchTerm.trim();
            }

            return SeSearchService.searchEvents(queryParams, {
                page,
                page_size: pageSize,
                paginate: paginateKey,
            });
        } catch (error) {
            console.error("Error searching SE events:", error);
            throw error;
        }
    }

    /**
     * Get Connect25 promoted events ({@code promoted_events=T}, category -1009).
     * Returns events within the next 4 months. Student Engagement scope is added server-side.
     *
     * @param page - Page number, 1-based (optional, default 1)
     * @param pageSize - Number of results per page (optional, default 20)
     * @param paginateKey - Pagination key from previous response (optional, default "T")
     * @returns Promise resolving to search response with promoted events
     */
    public static async getPromotedEvents(
        page: number = 1,
        pageSize: number = 20,
        paginateKey: string = "T",
    ): Promise<SESearchResponse<SEEventDocument>> {
        try {
            const now = new Date();
            const fourMonthsOut = new Date(now);
            fourMonthsOut.setMonth(fourMonthsOut.getMonth() + 4);

            const queryParams: Event.WsQueryParams = {
                promoted_events: "T",
                start_dt: S25Date.toS25ISODateStrStartOfDay(now),
                end_dt: S25Date.toS25ISODateStrEndOfDay(fourMonthsOut),
                order: "asc",
                sort: "start_date",
                state: [Event.State.Ids.Confirmed, Event.State.Ids.Tentative, Event.State.Ids.Draft],
            };

            return SeSearchService.searchEvents(queryParams, {
                page,
                page_size: pageSize,
                paginate: paginateKey,
            });
        } catch (error) {
            console.error("Error fetching promoted events:", error);
            throw error;
        }
    }

    /**
     * Get upcoming events from Student Engagement organizations.
     * Returns events with occurrences in the next 2 weeks.
     *
     * @param page - Page number, 1-based (optional, default 1)
     * @param pageSize - Number of results per page (optional, default 20)
     * @param paginateKey - Pagination key from previous response (optional, default "T")
     * @returns Promise resolving to search response with upcoming events
     */
    public static async getUpcomingEvents(
        page: number = 1,
        pageSize: number = 20,
        paginateKey: string = "T",
        additionalQueryParams: Event.WsQueryParams = {},
    ): Promise<SESearchResponse<SEEventDocument>> {
        try {
            const now = new Date();

            const queryParams: Event.WsQueryParams = {
                ...additionalQueryParams,
                category_id: [Category.SystemDefined.StudentEngagement],
                start_dt: S25Date.toS25ISODateStrStartOfDay(now),
                order: "asc",
                sort: "start_date",
                state: [Event.State.Ids.Confirmed, Event.State.Ids.Tentative, Event.State.Ids.Draft],
            };

            return SeSearchService.searchEvents(queryParams, {
                page,
                page_size: pageSize,
                paginate: paginateKey,
            });
        } catch (error) {
            console.error("Error fetching upcoming events:", error);
            throw error;
        }
    }

    public static myEventsQueryParams(): Event.WsQueryParams {
        return {
            category_id: [Category.SystemDefined.StudentEngagement],
            attendee_state: [attendanceState.INTERESTED, attendanceState.REGISTERED, attendanceState.PENDING],
        };
    }

    public static async createOrgAdvancedSearchId(steps: {
        organization_search: { search: { step: SearchCriteria.Step[] } };
    }): Promise<number | undefined> {
        const response = await DataAccess.post<{ organization_search: { search: { query_id: number } } }>(
            DataAccess.injectCaller("/se/org_search.json", "SeSearchService.createOrgAdvancedSearchId"),
            steps,
        );
        return S25ObjectUtil.propertyGet(S25Xml.prettifyJson(response), "queryId");
    }

    public static async putOrgAdvancedSearch(queryId: number, searchJson: unknown): Promise<void> {
        await DataAccess.put(
            DataAccess.injectCaller(`/se/org_search.json?query_id=${queryId}`, "SeSearchService.putOrgAdvancedSearch"),
            searchJson,
        );
    }

    public static async createOrgAdvancedSearch(steps: SearchCriteria.Step[]): Promise<number | undefined> {
        const queryId = await SeSearchService.createOrgAdvancedSearchId({
            organization_search: { search: { step: steps } },
        });

        if (queryId) {
            const name = `temp_${S25UUID.quickGUID()}`;

            for (let idx = 0; idx < steps.length; idx++) {
                steps[idx].step_number = idx + 1;
                steps[idx].status = "new";
                if (steps[idx].step_param) {
                    for (let paramIdx = 0; paramIdx < steps[idx].step_param.length; paramIdx++) {
                        steps[idx].step_param[paramIdx].step_param_nbr = paramIdx + 1;
                    }
                }
            }

            const searchJson = {
                organization_search: {
                    _engine: "accl",
                    _pubdate: S25Date.toS25ISODateTimeStr(new Date()),
                    search: {
                        query_id: queryId,
                        query_name: name,
                        query_method: "all",
                        query_type: "Private",
                        query_type_id: "1",
                        favorite: "F",
                        status: "new",
                        step: steps,
                    },
                },
            };

            await SeSearchService.putOrgAdvancedSearch(queryId, searchJson);

            return queryId;
        }
    }

    public static deleteSearch(queryId: number) {
        return DataAccess.delete(
            DataAccess.injectCaller(`/se/org_search.json?query_id=${queryId}`, "SeSearchService.deleteSearch"),
        );
    }
}
